import {
  getPeriodReferentialInterval,
  type PeriodGranularityType,
  type PeriodReferentialIntervalType
} from '#domain/core/filter/period';
import { useFiltersStore } from '#stores/filters.store';
import { useUserStore } from '#stores/user.store';
import { DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL } from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed, inject, ref, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { CARREFOUR_REFERENTIAL_INTERVAL_KEY } from '../constants/periodField.constant';

/**
 * Returns a resolver giving the refresh-state boundaries of any granularity. Must be called
 * during component setup: it reads the injected referential interval key.
 */
export function usePeriodReferentialIntervalResolver(): (
  granularity: PeriodGranularityType
) => PeriodReferentialIntervalType | null {
  const { filters } = storeToRefs(useFiltersStore());
  const { defaultKpisPerimeter } = storeToRefs(useUserStore());
  const referentialIntervalKey = inject(
    CARREFOUR_REFERENTIAL_INTERVAL_KEY,
    ref(DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL)
  );

  return (granularity) => {
    const referentialIntervals = filters.value.referentialIntervals ?? defaultKpisPerimeter.value.referentialIntervals;

    return getPeriodReferentialInterval(referentialIntervals, referentialIntervalKey.value, granularity);
  };
}

/**
 * Refresh-state boundaries of the granularity the field currently works with, or null while
 * the granularity is unknown.
 */
export function usePeriodReferentialInterval(
  granularity: MaybeRefOrGetter<PeriodGranularityType | undefined>
): ComputedRef<PeriodReferentialIntervalType | null> {
  const resolve = usePeriodReferentialIntervalResolver();

  return computed(() => {
    const currentGranularity = toValue(granularity);

    return currentGranularity ? resolve(currentGranularity) : null;
  });
}
